import { Plug, Radio } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { AnalyticsProvider, type AnalyticsDestination } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteDestinationDialog } from '../components/delete-destination-dialog';
import { DestinationCard, type DestinationAction } from '../components/destination-card';
import { DestinationDialog } from '../components/destination-dialog';
import { useUpdateAnalyticsDestination } from '../hooks/use-analytics-destination-mutations';
import { useAnalyticsDestinations } from '../hooks/use-analytics-destinations';

const PROVIDERS = Object.values(AnalyticsProvider);

type Editing =
  | { open: false }
  | { open: true; destination: null; provider: AnalyticsProvider }
  | { open: true; destination: AnalyticsDestination; focusCredential: boolean };

function Destinations() {
  const { t } = useTranslation('analytics');
  const store = useStore();
  const message = useErrorMessage();
  const query = useAnalyticsDestinations(store.id);
  const toggle = useUpdateAnalyticsDestination(store.id, null);
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [deleting, setDeleting] = useState<AnalyticsDestination | null>(null);
  const destinations = query.data ?? [];
  const available = PROVIDERS.filter((p) => !destinations.some((d) => d.provider === p));
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });

  const onToggle = (destination: AnalyticsDestination, active: boolean) =>
    toggle.mutate(
      { id: destination.id, body: { status: active ? 'active' : 'disabled' } },
      {
        onSuccess: () => toast.success(active ? t('toasts.resumed') : t('toasts.paused')),
        onError: (error) => toast.error(message(error)),
      },
    );

  const onAction = (action: DestinationAction, destination: AnalyticsDestination) => {
    if (action === 'delete') setDeleting(destination);
    else setEditing({ open: true, destination, focusCredential: action === 'rotate' });
  };

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
      />
      <ReadOnlyBanner reason={reason} />
      <Alert className="mb-4" data-tour="settings.analytics.intro">
        <Radio aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      {query.isPending ? (
        <Skeleton className="h-64" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="flex flex-col gap-4">
          {destinations.length === 0 ? (
            <EmptyState
              icon={Radio}
              title={t('empty.title')}
              description={t('empty.description')}
              tour="settings.analytics.empty"
            />
          ) : (
            <div className="flex flex-col gap-4" data-tour="settings.analytics.destination">
              {destinations.map((destination) => (
                <DestinationCard
                  key={destination.id}
                  storeId={store.id}
                  destination={destination}
                  editable={editable}
                  toggling={toggle.isPending && toggle.variables.id === destination.id}
                  onToggle={onToggle}
                  onAction={onAction}
                />
              ))}
            </div>
          )}
          {available.length > 0 && editable ? (
            <section
              aria-labelledby="analytics-available"
              className="flex flex-col gap-3"
              data-tour="settings.analytics.connect"
            >
              <div>
                <h2 id="analytics-available" className="text-[15px] font-semibold">
                  {t('available.title')}
                </h2>
                <p className="text-muted-foreground text-sm">{t('available.description')}</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {available.map((provider) => (
                  <Card key={provider} className="gap-3">
                    <CardHeader>
                      <CardTitle className="text-[15px]">{t(`providers.${provider}`)}</CardTitle>
                      <CardDescription>{t(`providerHints.${provider}`)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        onClick={() => setEditing({ open: true, destination: null, provider })}
                      >
                        <Plug aria-hidden />
                        {t('actions.connect', { provider: t(`providers.${provider}`) })}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
      {editing.open ? (
        <DestinationDialog
          key={editing.destination ? editing.destination.id : `new-${editing.provider}`}
          storeId={store.id}
          destination={editing.destination}
          available={
            editing.destination
              ? []
              : [editing.provider, ...available.filter((p) => p !== editing.provider)]
          }
          focusCredential={editing.destination ? editing.focusCredential : false}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <DeleteDestinationDialog
        storeId={store.id}
        destination={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function DestinationsPage() {
  return (
    <FeatureErrorBoundary name="analytics.destinations">
      <Destinations />
    </FeatureErrorBoundary>
  );
}
